import * as React from "react";
import { Button, Form } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Formik } from "formik";
import { AppConfiguration, PublicRoutes, QueryParamKeys, QueryParamValues, StatusCode } from "src/common";
import { Message } from "src/common/app/message-item.class";
import { MessageHandlerDisplayType } from "src/common/constants/constants";
import { DeveloperErrorMessages, DeveloperSuccessMessages, DeveloperWarningMessages, useCreateAccount } from "src/common/developers";
import { uiActions } from "src/store";
import * as yup from "yup";

function IndividualUserFormComponent() {
  const default_user_id = "testing_@developer.com".replace(/@/, self.crypto.randomUUID().slice(0, 4) + "@");
  const default_display_name = "Testing User";
  const dispatch = useDispatch();
  const { createUser, success, setSuccess } = useCreateAccount();

  const schema = yup.object().shape({
    user_id: yup.string().email("Invalid email").required("User ID is required"),
    display_name: yup.string().required("Display name is required"),
  });

  function handleError(error: any) {
    switch (error.status) {
      case StatusCode.FORBIDDEN_DOMAIN:
        dispatch(uiActions.handleRequestErrors(new Message(DeveloperErrorMessages.error_forbidden, MessageHandlerDisplayType.toastr)));
        break;
      case StatusCode.NO_EMAIL_SIGNATURE_CODE:
        dispatch(uiActions.handleRequestErrors(new Message(DeveloperErrorMessages.error_API_error, MessageHandlerDisplayType.toastr)));
        break;
      case StatusCode.CONFLICT_CODE:
        dispatch(uiActions.handleRequestErrors(new Message(DeveloperErrorMessages.error_user_already_exists, MessageHandlerDisplayType.toastr)));
        break;
      default:
        dispatch(uiActions.handleRequestErrors(new Message(DeveloperErrorMessages.error_creating_user, MessageHandlerDisplayType.toastr)));
    }
  }

  async function handleSingleSubmit(user_id: string, display_name: string) {
    const is_web = AppConfiguration.buildForWeb();
    if (is_web) {
      dispatch(uiActions.handleRequestErrors(new Message(DeveloperWarningMessages.warning_wrong_port, MessageHandlerDisplayType.toastr)));
      return;
    }
    try {
      await createUser(user_id, display_name);
      setSuccess(true);
      dispatch(uiActions.handleSetMessage(new Message(DeveloperSuccessMessages.success_creating_user)));
    } catch (error: any) {
      handleError(error);
    }
  }

  return (
    <Formik
      validationSchema={schema}
      initialValues={{ user_id: default_user_id, display_name: default_display_name }}
      onSubmit={async (values, { setSubmitting }) => {
        const user_id = values.user_id;
        const display_name = values.display_name;
        await handleSingleSubmit(user_id, display_name);
        setSubmitting(false);
      }}
    >
      {({ handleSubmit, handleChange, handleBlur, values, errors, touched, isSubmitting }) => (
        <Form noValidate onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="user_id">User ID</Form.Label>
            <Form.Control
              id="user_id"
              name="user_id"
              type="email"
              placeholder={default_user_id}
              value={values.user_id}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!!errors.user_id && touched.user_id}
            />
            <Form.Control.Feedback type="invalid">
              {errors.user_id}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="display_name">Display Name</Form.Label>
            <Form.Control
              id="display_name"
              name="display_name"
              type="text"
              placeholder={default_display_name}
              value={values.display_name}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={!!errors.display_name && touched.display_name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.display_name}
            </Form.Control.Feedback>
          </Form.Group>
          <Button type="submit" className="me-2" disabled={isSubmitting}>Create User</Button>
          {success && <Button variant="link" href={`/${PublicRoutes.choose_account_route}?${QueryParamKeys.PAGE_ACTION_QUERY_KEY}=${QueryParamValues.switch}`}>Choose Account</Button>}
        </Form>
      )}
    </Formik>
  );
}

export default React.memo(IndividualUserFormComponent); 
